import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { OpenFeatureProvider } from '@openfeature/react-sdk';

import App from './App';
import { FeatureFlagsSetup } from './featureFlags';
import './assets/fonts/DMSans-Regular.ttf';
import { AUTH0_AUDIENCE, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from './constants';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { store } from './store';
import './i18n';

import { APP_ENV } from './constants';

if (APP_ENV && APP_ENV !== 'localhost') {
  Sentry.init({
    dsn: 'https://f79800f7b55046c1b3299f7ed22f3723@o1232657.ingest.sentry.io/4503892457488384',
    environment: APP_ENV,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        // See docs for support of different versions of variation of react router
        //  https://docs.sentry.io/platforms/javascript/performance/instrumentation/automatic-instrumentation/
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      Sentry.replayIntegration({
        // mask all text data
        blockAllMedia: false,
        // block all media (consider performance and user experience)
      }),
    ],

    replaysSessionSampleRate: 0,
    // Capture all sessions when an error occurs
    replaysOnErrorSampleRate: 1.0,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,
    ignoreErrors: ['Login required', 'login required'],
    beforeSend(event, hint) {
      const exception: any = hint.originalException;
      // Drop events here, if they are not a valid exception
      if (exception?.message?.includes('Login required')) {
        return null;
      }
      return event;
    },
  });
}

const container = document.getElementById('root')!;
const root = createRoot(container);

root.render(
  // Enable only during development. Comment it when pushing to main as this will fail the CI pipelines
  // <React.StrictMode>
  <Provider store={store}>
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE,
      }}
      onRedirectCallback={(appState) => {
        // appState doesn't contain returnTo url, do not reload the app
        if (!appState?.returnTo) return;
        window.location.replace(appState?.returnTo);
      }}
    >
      <FeatureFlagsSetup>
        <OpenFeatureProvider>
          <App />
        </OpenFeatureProvider>
      </FeatureFlagsSetup>
    </Auth0Provider>
  </Provider>,
  // </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
